import { InputNumber } from "antd";
import currency from "currency.js";
import styled from "styled-components";
import UdfBaseRangeField from "./UdfBaseRangeField";

const StyledInputNumber = styled(InputNumber)`
  flex: 1;
  margin-right: 8px;
`;

/**
 * Format decimal number for display (without currency symbol)
 */
function formatDecimal(value) {
  if (value == null) return "";
  return currency(value, { precision: 2, symbol: "" }).format();
}

/**
 * Display component for decimal range values
 * Handles: arrays, full range, min only (≥), max only (≤), single value
 */
function UdfDecimalRangeDisplay({ value }) {
  // Handle array of values
  if (Array.isArray(value)) {
    return (
      <>
        {value.map((val, index) => (
          <span key={index}>
            <UdfDecimalRangeDisplay value={val} />
            {index < value.length - 1 && ", "}
          </span>
        ))}
      </>
    );
  }

  // Handle range object
  if (value && typeof value === "object") {
    if (value.min_value != null && value.max_value != null) {
      return (
        <>
          {formatDecimal(value.min_value)} - {formatDecimal(value.max_value)}
        </>
      );
    }
    if (value.min_value != null) {
      return <>≥ {formatDecimal(value.min_value)}</>;
    }
    if (value.max_value != null) {
      return <>≤ {formatDecimal(value.max_value)}</>;
    }
  }

  // Fallback: primitive value
  return formatDecimal(value);
}

/**
 * Validate decimal range: min <= max
 */
function validateDecimalRange(min, max) {
  if (min == null || max == null) return true; // Partial range OK
  return min <= max;
}

/**
 * Decimal range input component for UDF filters
 */
function UdfDecimalRangeEdit({ addValue, currentValue, setCurrentValue, canAddCurrentValue }) {
  return (
    <UdfBaseRangeField
      minValue={currentValue?.min_value}
      maxValue={currentValue?.max_value}
      onValueChange={setCurrentValue}
      onAddValue={() => addValue(currentValue)}
      minKey="min_value"
      maxKey="max_value"
      minLabel="Min"
      maxLabel="Max"
      validateRange={validateDecimalRange}
      canAddValue={canAddCurrentValue}
      renderMinInput={(props) => <StyledInputNumber {...props} step={0.01} precision={2} />}
      renderMaxInput={(props) => <StyledInputNumber {...props} step={0.01} precision={2} />}
    />
  );
}

/**
 * Factory function that returns both Edit and Display components
 */
function UdfDecimalRange() {
  return {
    Edit: UdfDecimalRangeEdit,
    Display: UdfDecimalRangeDisplay,
  };
}

export default UdfDecimalRange;
